// hands-free.ts — VAD (Voice Activity Detection) fuer den Freihand-Modus (#303).
// Ueberwacht den Mikro-Pegel via AudioMeter: erkennt Sprechbeginn und -ende
// (anhaltende Stille NACH Sprache) und meldet "fertig gesprochen" an den
// Aufrufer -> Auto-Senden. So "reagiert" Vespera selbst, ohne Tippen.
//
// Schwellwerte/Timing sind bewusst Konstanten oben — sie brauchen On-Device-
// Feinabstimmung (Mikro-Abstand, Umgebungsgeraeusch Laptop vs iPhone).
import { AudioMeter } from "./audio-meter.js";

// 2026-07-09 (Martin, 'Hoere zu, reagiert auf nichts'): Der PTT-Stream ist
// ROH-Audio ohne autoGainControl (Tempo-Fix) -> der reale Pegel liegt weit
// unter den alten festen Schwellen. Deshalb ADAPTIV: die ersten ~0.8s nach
// dem Start messen das Grundrauschen (floor), die Schwellen werden relativ
// dazu gesetzt — funktioniert auf iPhone UND Laptop, egal wie leise das Mikro.
const CALIBRATION_FRAMES = 45; // ~0.75s bei 60fps
const SPEECH_FLOOR_FACTOR = 3.0; // Sprache = deutlich ueber Grundrauschen
const SPEECH_MIN = 0.08; // Untergrenze — 0.035 triggerte auf Atem/Rauschen (peak 0.046), echtes Sprechen liegt bei 0.2-0.6
const SPEECH_SUSTAIN_FRAMES = 10; // ~170ms anhaltend ueber der Schwelle = Sprache (kein Einzel-Ausschlag)
const SPEECH_MAX = 0.3; // Obergrenze (lautes Grundrauschen deckelt nicht alles)
export const SILENCE_MS = 2200; // Stille NACH Sprache = "fertig" — 1.3s schickte mitten im Nachdenken ab (Martin 10.07.)
const MIN_SPEECH_MS = 500; // Mindest-Sprechdauer (ein Huster sendet nicht)

export interface HandsFreeCallbacks {
  /** Genug gesprochen + anhaltende Pause -> jetzt senden. */
  onSpeechEnd: () => void;
  /** Optional fuer eine Pegel-Anzeige. */
  onLevel?: (level: number) => void;
}

export class HandsFreeVad {
  private meter = new AudioMeter();
  private cb: HandsFreeCallbacks | null = null;
  private speechStart = 0;
  private hadSpeech = false;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private active = false;
  // Adaptive Kalibrierung + Diagnose
  private calibSamples: number[] = [];
  private floor = 0;
  private speechThresh = SPEECH_MIN;
  private silenceThresh = SPEECH_MIN * 0.5;
  private peak = 0;
  private speechDetected = false;
  private frames = 0;
  private speechFrames = 0;
  private attachError = "";
  private deviceLabel = "";
  private deviceMuted = false;
  private silenceWarned = false;
  /** Optionaler Melder fuer die Dauer-Stille-Warnung (Toast im UI). */
  public onSilentInput: (() => void) | null = null;
  // 2026-07-09 ([vad-t]: peak=0.000): iOS liefert einem ZWEITEN Konsumenten
  // desselben Tracks nur Stille — wir messen deshalb auf einem KLON.
  private clonedTrack: MediaStreamTrack | null = null;

  /** 20.10.2026 (Vollständigkeits-Bremse, app.ts `freihandGebremst`): läuft gerade eine neue Äußerung? */
  sprichtGerade(): boolean {
    return this.active && this.hadSpeech;
  }

  /** Diagnose fuer die [vad-t]-Telemetrie. */
  getStats(): {
    floor: number;
    speechThresh: number;
    peak: number;
    speechDetected: boolean;
    calibrated: boolean;
    frames: number;
    attachError: string;
    deviceLabel: string;
    deviceMuted: boolean;
  } {
    return {
      floor: this.floor,
      speechThresh: this.speechThresh,
      peak: this.peak,
      speechDetected: this.speechDetected,
      calibrated: this.calibSamples.length >= CALIBRATION_FRAMES,
      frames: this.frames,
      attachError: this.attachError,
      deviceLabel: this.deviceLabel,
      deviceMuted: this.deviceMuted,
    };
  }

  /** Startet die Erkennung auf dem gegebenen Mikro-Track. */
  start(track: MediaStreamTrack | null, cb: HandsFreeCallbacks): void {
    this.stop();
    if (!track) {
      // Nie wieder still scheitern — [vad-t] zeigt das als err=no-track.
      this.attachError = "no-track";
      return;
    }
    this.cb = cb;
    this.active = true;
    this.hadSpeech = false;
    this.speechStart = 0;
    this.speechFrames = 0;
    this.calibSamples = [];
    this.floor = 0;
    this.speechThresh = SPEECH_MIN;
    this.silenceThresh = SPEECH_MIN * 0.5;
    this.peak = 0;
    this.speechDetected = false;
    this.frames = 0;
    this.attachError = "";
    // iOS-Workaround: Klon als eigener Konsument (Original bleibt beim
    // MediaRecorder). Faellt der Klon aus, notieren wir den Fehler fuer [vad-t].
    // Selbst-Diagnose (peak=0.002-Vorfall): welches Eingangsgeraet liefert?
    this.deviceLabel = track.label || "?";
    this.deviceMuted = track.muted === true;
    this.silenceWarned = false;
    let meterTrack: MediaStreamTrack = track;
    try {
      this.clonedTrack = track.clone();
      meterTrack = this.clonedTrack;
    } catch (e) {
      this.attachError = "clone:" + (e instanceof Error ? e.message : String(e));
    }
    try {
      this.meter.attach(meterTrack, (level: number) => {
      this.cb?.onLevel?.(level);
      if (!this.active) return;
      this.frames += 1;
      if (level > this.peak) this.peak = level;
      // Kalibrierphase: Grundrauschen messen, Schwellen relativ setzen.
      if (this.calibSamples.length < CALIBRATION_FRAMES) {
        this.calibSamples.push(level);
        if (this.calibSamples.length === CALIBRATION_FRAMES) {
          const sorted = [...this.calibSamples].sort((a, b) => a - b);
          this.floor = sorted[Math.floor(sorted.length / 2)] ?? 0;
          this.speechThresh = Math.min(
            SPEECH_MAX,
            Math.max(SPEECH_MIN, this.floor * SPEECH_FLOOR_FACTOR),
          );
          this.silenceThresh =
            this.floor + (this.speechThresh - this.floor) * 0.45;
        }
        return; // waehrend der Kalibrierung noch nicht auswerten
      }
      const now = Date.now();
      // Dauer-Stille nach der Kalibrierung: Mikro liefert kein Signal
      // (falsches Eingangsgeraet / stumm) -> EINMAL sichtbar melden statt
      // dass "Hoere zu" stumm gluht (~6s nach Kalibrierung).
      if (
        !this.silenceWarned &&
        !this.speechDetected &&
        this.frames > CALIBRATION_FRAMES + 360 &&
        this.peak < 0.01
      ) {
        this.silenceWarned = true;
        try {
          if (this.cb) this.onSilentInput?.();
        } catch {
          /* Melder best-effort */
        }
      }
      if (level >= this.speechThresh) {
        // Anhaltend ueber der Schwelle noetig — Einzel-Ausschlaege (Atem,
        // Klick) starten keine Aeusserung mehr.
        this.speechFrames += 1;
        if (!this.hadSpeech && this.speechFrames >= SPEECH_SUSTAIN_FRAMES) {
          this.hadSpeech = true;
          this.speechDetected = true;
          this.speechStart = now;
        }
        if (this.hadSpeech) this.clearSilence();
      } else if (
        level <= this.silenceThresh &&
        this.hadSpeech &&
        !this.silenceTimer &&
        now - this.speechStart >= MIN_SPEECH_MS
      ) {
        // Genug gesprochen + jetzt Stille -> bei Fortdauer senden.
        this.silenceTimer = setTimeout(() => {
          this.silenceTimer = null;
          if (this.active && this.hadSpeech) {
            this.hadSpeech = false;
            this.speechFrames = 0;
            this.cb?.onSpeechEnd();
          }
        }, SILENCE_MS);
      }
      });
    } catch (e) {
      this.attachError =
        (this.attachError ? this.attachError + "|" : "") +
        "attach:" + (e instanceof Error ? e.message : String(e));
    }
  }

  private clearSilence(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  /** Erkennung pausieren (z.B. waehrend Vespera spricht) — Meter bleibt attached. */
  pause(): void {
    this.active = false;
    this.clearSilence();
  }

  /** Erkennung fortsetzen; startet eine frische Aeusserung. */
  resume(): void {
    this.hadSpeech = false;
    this.speechStart = 0;
    this.speechFrames = 0;
    this.active = true;
  }

  /** Alles loesen. */
  stop(): void {
    this.active = false;
    this.clearSilence();
    this.meter.detach();
    // Klon explizit beenden — er haelt sonst das Mikro (oranger Punkt).
    try {
      this.clonedTrack?.stop();
    } catch {
      /* ignore */
    }
    this.clonedTrack = null;
    this.cb = null;
    this.hadSpeech = false;
  }

  isRunning(): boolean {
    return this.cb !== null;
  }
}
